import { Trans, useTranslation } from 'react-i18next';
import { TrendingUp } from 'react-feather';
import { differenceInMinutes, isFuture } from 'date-fns';

import {
  DiscountStrategy,
  GetEventQuery as GetEvent,
} from '__generated__/graphql';

import UI from '../../common/UI';
import useLocale from '../../common/useLocale';

type Discount = GetEvent['event']['ticket_categories'][0]['tickets_for_sale']['0']['current_discount'];
// eslint-disable-next-line max-len
type DiscountLevel = GetEvent['event']['ticket_categories'][0]['tickets_for_sale']['0']['current_discount']['levels'][0];

export interface ScheduledDiscountLabelProps {
  discount: Discount;
  promotions: { end_date?: string | null; }[];
}

const ScheduledDiscountLabel = ({ discount, promotions }: ScheduledDiscountLabelProps) => {
  // Explicitly use frontend namespace, because this component is also used in the participant dashboard
  const { t } = useTranslation('frontend');
  const { formatNumber, parseDate } = useLocale();

  const { shouldRender, minutesLeft, hoursLeft, daysLeft } = getScheduledDiscountLabelVisibility({
    discount,
    promotions,
    parseDate,
  });

  if (!shouldRender) {
    return <></>;
  }

  return (
    <UI.Label>
      <UI.Icon>
        <TrendingUp />
      </UI.Icon>
      {' '}
      <Trans
        i18nKey="frontend:selector.price_increase_in_days"
        values={{
          timeLeft: hoursLeft > 48
            ? t('selector.n_days', {
              count: daysLeft,
              value: formatNumber(daysLeft),
            })
            : (minutesLeft > 60
              ? t('selector.n_hours', {
                count: hoursLeft,
                value: formatNumber(hoursLeft),
              })
              : t('selector.n_minutes', {
                count: minutesLeft,
                value: formatNumber(minutesLeft),
              })),
        }}
      >
        <UI.Strong />
      </Trans>
    </UI.Label>
  );
};

interface ParseDateOptions {
  timezone?: string | null;
}

type ParseDate = (date: string, options?: ParseDateOptions) => Date;

interface ScheduledDiscountLabelVisibilityInput {
  discount: Discount;
  promotions: { end_date?: string | null; }[];
  parseDate: ParseDate;
  now?: Date;
  maxDaysLeft?: number;
}

export interface ScheduledDiscountLabelVisibility {
  shouldRender: boolean;
  minutesLeft: number;
  hoursLeft: number;
  daysLeft: number;
}

export const getScheduledDiscountLabelVisibility = ({
  discount,
  promotions,
  parseDate,
  now = new Date(),
  maxDaysLeft = 7,
}: ScheduledDiscountLabelVisibilityInput): ScheduledDiscountLabelVisibility => {
  if (discount.strategy !== DiscountStrategy.scheduled) {
    return {
      shouldRender: false,
      minutesLeft: 0,
      hoursLeft: 0,
      daysLeft: 0,
    };
  }

  const levelIsActive = (level: DiscountLevel) => (
    level.end_date !== null && isFuture(parseDate(level.end_date, { timezone: 'UTC' }))
  );

  const activeLevelIndex = discount.levels.findIndex(levelIsActive);
  const activeLevel = discount.levels[activeLevelIndex];

  if (!activeLevel || activeLevel.end_date === null) {
    return {
      shouldRender: false,
      minutesLeft: 0,
      hoursLeft: 0,
      daysLeft: 0,
    };
  }

  const nextLevel = discount.levels[activeLevelIndex + 1];

  if (nextLevel && nextLevel.value >= activeLevel.value) {
    return {
      shouldRender: false,
      minutesLeft: 0,
      hoursLeft: 0,
      daysLeft: 0,
    };
  }

  const salesEndAfterDiscountEnds = promotions.reduce<boolean>((salesEnd, promotion) => (
    salesEnd || !promotion.end_date || promotion.end_date > activeLevel.end_date
  ), false);

  if (!salesEndAfterDiscountEnds) {
    return {
      shouldRender: false,
      minutesLeft: 0,
      hoursLeft: 0,
      daysLeft: 0,
    };
  }

  const minutesLeft = differenceInMinutes(parseDate(activeLevel.end_date, { timezone: 'UTC' }), now);
  const hoursLeft = Math.round(minutesLeft / 60);
  const daysLeft = Math.round(hoursLeft / 24);

  return {
    shouldRender: daysLeft <= maxDaysLeft,
    minutesLeft,
    hoursLeft,
    daysLeft,
  };
};

export const shouldRenderScheduledDiscountLabel = (input: ScheduledDiscountLabelVisibilityInput): boolean => (
  getScheduledDiscountLabelVisibility(input).shouldRender
);

export default ScheduledDiscountLabel;
